import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { Tooltip as InfoTooltip } from "react-tooltip";
import { Info } from "lucide-react";

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #000;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const parseAmount = (value) => {
  if (!value) return 0;
  return (
    parseFloat(
      value.toString().replace(/\s/g, "").replace(",", ".").replace(/[^\d.-]/g, "")
    ) || 0
  );
};

const getWeekNumber = (dateStr) => {
  const date = new Date(dateStr);
  if (isNaN(date)) return null;
  const firstJan = new Date(date.getFullYear(), 0, 1);
  const pastDays = (date - firstJan) / 86400000;
  return Math.ceil((pastDays + firstJan.getDay() + 1) / 7);
};

const WeekPurchaseDetailsChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const years = Array.from(
      new Set(
        data
          .map((row) => {
            const date = new Date(row.DataZakupu);
            return isNaN(date) ? null : date.getFullYear();
          })
          .filter(Boolean)
      )
    );

    const latestYear = Math.max(...years);
    setAvailableYears(years.sort((a, b) => b - a));
    if (!selectedYear) setSelectedYear(latestYear);
  }, [data]);

  useEffect(() => {
    if (!selectedYear) return;

    const weeks = new Set();
    data.forEach((row) => {
      const date = new Date(row.DataZakupu);
      if (!isNaN(date) && date.getFullYear() === selectedYear) {
        const week = getWeekNumber(row.DataZakupu);
        if (week) weeks.add(week);
      }
    });

    const sortedWeeks = Array.from(weeks).sort((a, b) => a - b);
    setAvailableWeeks(sortedWeeks);
    if (!selectedWeek && sortedWeeks.length > 0) setSelectedWeek(sortedWeeks[0]);
  }, [selectedYear, data]);

  useEffect(() => {
    if (!selectedWeek || !selectedYear) return;

    const getDailySums = (year) => {
      const sums = Array(7).fill(0); // Monday=0 to Sunday=6
      data.forEach((row) => {
        const date = new Date(row.DataZakupu);
        if (isNaN(date)) return;

        const rowYear = date.getFullYear();
        const rowWeek = getWeekNumber(row.DataZakupu);

        if (rowYear === year && rowWeek === selectedWeek) {
          const day = (date.getDay() + 6) % 7; // shift Sunday to 6
          sums[day] += parseAmount(row.K_42);
        }
      });
      return sums;
    };

    const current = getDailySums(selectedYear);
    const previous = getDailySums(selectedYear - 1);

// replace the setChartData({...}) block with this version
setChartData({
  labels: ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Nd"],
  datasets: [
    {
      label: `Zakupy (${selectedYear})`,
      data: current,
      type: "bar",
      backgroundColor: "rgba(152, 72, 199, 0.9)", // current year = your purple
      borderColor: "rgba(152, 72, 199, 1)",
      borderWidth: 1,
      order: 2, // draw on top
    },
    {
      label: `Zakupy (${selectedYear - 1})`,
      data: previous,
      type: "bar",
      backgroundColor: "rgba(220, 53, 69, 0.45)", // previous year = red
      borderColor: "rgba(220, 53, 69, 1)",
      borderWidth: 1,
      order: 1,
    },
  ],
});


  }, [selectedWeek, selectedYear, data]);

  if (!chartData) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="week-purchase-tooltip">
        <Info />
      </InfoIconWrapper>

      <InfoTooltip
        id="week-purchase-tooltip"
        place="left"
        content={
          <>
            <strong>Zakupy według dnia trygodnia</strong>
            <br />
            Porównanie wartości zakupów dla tych samych dni tygodnia
            <br />
            w tygodniu {selectedWeek} ({selectedYear} vs {selectedYear - 1})
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="year-select">Rok:</label>
          <select
            id="year-select"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="week-select">Tydzień:</label>
          <select
            id="week-select"
            value={selectedWeek || ""}
            onChange={(e) => setSelectedWeek(parseInt(e.target.value, 10))}
          >
            {availableWeeks.map((week) => (
              <option key={week} value={week}>
                {week}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      <Bar
        data={chartData}
        options={{
          responsive: true,
          maintainAspectRatio: false,
plugins: {
  title: {
    display: true,
    text: `Zakupy według dnia trygodnia – tydzień ${selectedWeek}`,
  },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const value = context.parsed.y || 0;
                  return `${context.dataset.label}: ${value.toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`;
                },
              },
            },
            legend: {
              position: "top",
            },
          },
          scales: {
            x: {
              stacked: false,
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Zakupy (PLN)",
              },
              ticks: {
                callback: (val) =>
                  Number(val).toLocaleString("pl-PL", { minimumFractionDigits: 0 }),
              },
            },
          },
        }}
      />
    </ChartWrapper>
  );
};

export default WeekPurchaseDetailsChart;
